import { alpha, Button as MuiButton, ButtonProps as MuiButtonProps, styled } from '@mui/material';
import React from 'react';
import { EColors, getColor } from 'theme';

type BizlyButtonVariant = 'primary' | 'secondary' | 'outline' | 'text';
type BizlyButtonColor = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

type BizlyButtonProps = Omit<MuiButtonProps, 'variant' | 'color' | 'size'> & {
    variant?: BizlyButtonVariant;
    color?: BizlyButtonColor;
    shape?: 'rounded' | 'square';
    size?: 'small' | 'medium' | 'large';
    textDecoration?: 'underline' | 'none';
};

const StyledBizlyOSButton = styled(MuiButton, {
    shouldForwardProp: prop =>
        prop !== 'variant' && prop !== 'color' && prop !== 'size' && prop !== 'shape' && prop !== 'textDecoration',
})<BizlyButtonProps>(({
    theme,
    variant = 'primary',
    color = 'primary',
    shape = 'rounded',
    size = 'medium',
    textDecoration = 'underline',
}) => {
    const baseStyles = {
        height: size === 'small' ? '40px' : size === 'large' ? '56px' : '44px',
        fontSize: size === 'small' ? '0.875rem' : size === 'large' ? '1.125rem' : '1rem',
        lineHeight: '1rem',
        fontWeight: 500,
        fontFamily: '"General Sans"',
        borderRadius: shape === 'rounded' ? '23px' : '6px',
        padding: '0 16px',
    };

    const lightColor = getColor(EColors.pureWhite);
    let darkColor = getColor(EColors.v3Dark);

    if (color === 'error') {
        darkColor = getColor(EColors.v3Error);
    }

    if (color === 'success') {
        darkColor = getColor(EColors.v3Success);
    }

    if (color === 'warning') {
        darkColor = getColor(EColors.v3AccentColor);
    }

    if (color === 'secondary') {
        darkColor = getColor(EColors.v3TextSecondary);
    }

    const primaryStyles = {
        backgroundColor: darkColor,
        color: lightColor,
        '&:hover': { backgroundColor: alpha(darkColor, 0.8) },
    };

    const outlineStyles = {
        backgroundColor: lightColor,
        color: darkColor,
        border: '1px solid ' + darkColor,
        '&:hover': { backgroundColor: alpha(darkColor, 0.1) },
    };

    const textStyles = {
        color: darkColor,
        textDecoration,
        '&:hover': { backgroundColor: alpha(darkColor, 0.1) },
    };

    const disabledStyles = {
        '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
            borderColor: theme.palette.action.disabledBackground,
        },
    };

    const variantStyleMap: Record<BizlyButtonVariant, React.CSSProperties> = {
        primary: primaryStyles,
        secondary: {},
        outline: outlineStyles,
        text: textStyles,
    };

    const variantStyles = variantStyleMap[variant as BizlyButtonVariant];

    return { ...baseStyles, ...disabledStyles, ...variantStyles };
});

export const Button = StyledBizlyOSButton as React.FC<BizlyButtonProps>;
